import { HistoryFold, splitTimeline } from './Timeline';
import { badgeWord, pointText } from './timeline';
import { BADGE_LABELS, daysAnswerText, shortDateTime, type Recipient } from './types';
import './requests.css';

/**
 * The answer of one person asked about the event: "czeka" until they answer, then "mogę" / "nie mogę" / "pytanie" /
 * „część dni” (stage 2: with the answers per day), with the person's note. „nieaktualne — zapytaj o nową datę” when the
 * event's dates changed after the question. Nothing when the role may not see the answer.
 * Owner 2026-10-28 21:27: when it was answered („odpowiedź 28.10.2026, 21:15”) — only with the field „Data odpowiedzi”.
 * Owner 2026-10-30 21:31–21:38 („nie chciałbym abyś edytował wcześniejsze odpowiedzi na liście”): the NEWEST point of the
 * correspondence — an answer as given, or a later change of the day („zmiana dostępności — ręcznie: Szef”) — and the
 * earlier points folded under „Historia (N)”, as they were. Never an answer rewritten in place.
 */
export function AnswersBadge({ recipient }: { recipient: Recipient }) {
  if (!('answer' in recipient)) return null;
  const { latest, earlier } = splitTimeline(recipient.timeline);
  if (latest) {
    const t = pointText(latest);
    const label = recipient.stale ? 'nieaktualne — zapytaj o nową datę' : badgeWord(latest);
    const tone = recipient.stale ? 'nieaktualne' : latest.kind === 'answer' ? (latest.answer ?? 'czeka') : latest.kind === 'sent' ? 'czeka' : 'zmiana';
    const detail = [t.who, t.detail].filter((s) => s !== '').join(': ');
    const when = latest.kind === 'sent' ? (t.when ? `zapytano ${t.when}` : '') : latest.kind === 'answer' ? (t.when ? `odpowiedź ${t.when}` : '') : t.when;
    return (
      <span className="answer-badge-wrap" data-feature-id="requests.answers">
        <span className="answer-badge" data-answer={tone} title={[`Zapytanie o dostępność: ${label}`, detail, when].filter((s) => s !== '').join(', ')}>
          <span className="visually-hidden">Odpowiedź na zapytanie: </span>
          {label}
        </span>
        {detail && latest.kind !== 'sent' && <span className="answer-days-text">{detail}</span>}
        {when && latest.kind !== 'sent' && <span className="answer-when">{when}</span>}
        <HistoryFold points={earlier} />
      </span>
    );
  }
  // a role that may see the answer but not its history (or an older server): the answer as before
  const answer = recipient.answer ?? null;
  const label = recipient.stale ? 'nieaktualne — zapytaj o nową datę' : answer ? BADGE_LABELS[answer] : 'czeka';
  const when = answer ? (recipient.answeredAt ? `odpowiedź ${shortDateTime(recipient.answeredAt)}` : '') : recipient.sentAt ? `zapytano ${shortDateTime(recipient.sentAt)}` : '';
  const perDay = (recipient.days ?? []).some((d) => typeof d.answer === 'boolean') ? daysAnswerText(recipient.days!) : '';
  const title = [`Zapytanie o dostępność: ${label}`, perDay, when].filter((s) => s !== '').join(', ');
  return (
    <span className="answer-badge-wrap" data-feature-id="requests.answers">
      <span className="answer-badge" data-answer={recipient.stale ? 'nieaktualne' : (answer ?? 'czeka')} title={title}>
        <span className="visually-hidden">Odpowiedź na zapytanie: </span>
        {label}
      </span>
      {answer === 'czesciowo' && perDay && <span className="answer-days-text">{perDay}</span>}
      {recipient.answerNote ? <span className="answer-note">„{recipient.answerNote}”</span> : null}
      {answer && recipient.answeredAt ? <span className="answer-when">{when}</span> : null}
    </span>
  );
}
